import React, { useEffect } from 'react';
import { 
  Box, Flex, Text, VStack, HStack, Avatar, Image, Icon, 
  useColorModeValue, Divider, IconButton, Drawer, DrawerOverlay, 
  DrawerContent, DrawerCloseButton, useDisclosure, useBreakpointValue,
  CloseButton
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdDashboard, MdGroupWork, MdLogout, MdMenu } from 'react-icons/md';
import { FaChevronRight } from 'react-icons/fa';

const SidebarItem = ({ icon, label, to, isActive, isMobile }) => {
  const navigate = useNavigate();
  const textColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const activeBg = useColorModeValue('purple.50', 'purple.900');
  const activeTextColor = useColorModeValue('purple.600', 'purple.200');

  return (
    <Flex
      align="center"
      p="3"
      mx="2"
      borderRadius="lg"
      role="group"
      cursor="pointer"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeTextColor : textColor}
      _hover={{
        bg: isActive ? activeBg : hoverBg,
        color: isActive ? activeTextColor : textColor,
      }}
      transition="all 0.2s"
      onClick={() => {
        to && navigate(to);
        if (isMobile && window.onCloseMobileSidebar) {
          window.onCloseMobileSidebar();
        }
      }}
    >
      <Icon 
        as={icon} 
        fontSize={{ base: "24px", lg: "20px" }}
        mr="3"
        color={isActive ? activeTextColor : 'gray.500'}
      />
      <Text fontSize={{ base: "md", lg: "sm" }} fontWeight={isActive ? "semibold" : "medium"}>{label}</Text>
      {isActive && (
        <Icon 
          as={FaChevronRight} 
          fontSize={{ base: "16px", lg: "14px" }}
          ml="auto" 
          color={activeTextColor}
        />
      )}
    </Flex>
  );
};

const AssociadoSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  
  const isMobile = useBreakpointValue({ base: true, lg: false });
  
  // Make onClose available globally for mobile
  useEffect(() => {
    if (isMobile) {
      window.onCloseMobileSidebar = onClose;
    }
    return () => {
      if (window.onCloseMobileSidebar) {
        delete window.onCloseMobileSidebar;
      }
    };
  }, [isMobile, onClose]);

  const textColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const bgSidebar = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');

  const logoSrc = useColorModeValue(
    '/Assets/Logos/logo_mini.png',
    '/Assets/Logos/Logo-night.png'
  );

  const activeTextColor = useColorModeValue('brand.primary', 'brand.secondary');

  const logout = () => {
    localStorage.removeItem('userInfo');
    localStorage.removeItem('sul_investimentos_token');
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isActiveParent = (prefix) => {
    return location.pathname.startsWith(prefix);
  };

  const scrollbarThumbColor = useColorModeValue('gray.300', 'gray.600');

  const SidebarContent = () => (
    <Box 
      bg={bgSidebar} 
      w={{ base: "100%", lg: "250px" }}
      h="100vh" 
      py="5" 
      borderRight={{ base: "none", lg: "1px" }}
      borderColor={borderColor}
      position={{ base: "relative", lg: "fixed" }}
      left="0"
      top="0"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollbarThumbColor,
          borderRadius: '24px',
        },
      }}
      shadow={{ base: "none", lg: "lg" }}
      transition="all 0.2s"
      zIndex="1000"
    >
      <Flex 
        direction="column" 
        h="full" 
        justify="space-between"
      >
        <Box>
          <Flex 
            mb="3" 
            px="75" 
            alignItems="center" 
            justifyContent="space-between"
          >
            <Box maxW="100%">
              <Image 
                src={logoSrc}
                alt="Logo"
                maxH="75px"
                objectFit="contain"
              />
            </Box>
            {isMobile && (
              <CloseButton onClick={onClose} size="md" />
            )}
          </Flex>
          
          <HStack mb="6" px="5" spacing={3}>
            <Avatar 
              size="sm" 
              name={userInfo?.name || 'Associado'}
              src={userInfo?.avatar || ''}
              bg="green.500"
              color="white"
            />
            <Box>
              <Text 
                fontSize="sm" 
                fontWeight="medium"
                color={textColor}
                noOfLines={1}
              >
                {userInfo?.name || 'Associado'}
              </Text>
              <Text 
                fontSize="xs" 
                color="gray.500"
                noOfLines={1}
              >
                Associado
              </Text>
            </Box>
          </HStack>
          
          <Divider mb="4" />
          
          <Text 
            px="5" 
            my="3" 
            color="gray.500" 
            fontSize="xs" 
            fontWeight="medium"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Menu Principal
          </Text>

          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdDashboard} 
                label="Dashboard" 
                to="/associado/dashboard" 
                isActive={isActive('/associado/dashboard')}
                isMobile={isMobile}
              />
            </Box>

            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdGroupWork} 
                label="Agrupamento de Precatórios" 
                to="/associado/precatorio-groups" 
                isActive={isActiveParent('/associado/precatorio-groups')}
                isMobile={isMobile}
              />
            </Box>
          </VStack>
        </Box>

        <Box px="5" pb="3">
          <Divider mb="4" />
          <Flex
            align="center"
            p="3"
            borderRadius="lg"
            cursor="pointer"
            _hover={{ bg: hoverBg }}
            transition="all 0.2s"
            onClick={logout}
          >
            <Icon as={MdLogout} fontSize="20px" mr="3" color="red.500" />
            <Text fontSize="sm" fontWeight="medium" color="red.500">
              Sair
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            display={{ base: "flex", lg: "none" }}
            onClick={onOpen}
            variant="outline"
            aria-label="open menu"
            icon={<MdMenu />}
            position="fixed"
            top="4"
            left="4"
            zIndex="999"
            bg={bgSidebar}
            shadow="md"
          />

          <Drawer
            isOpen={isOpen}
            placement="left"
            onClose={onClose}
            returnFocusOnClose={false}
            onOverlayClick={onClose}
            size="full"
          >
            <DrawerOverlay />
            <DrawerContent maxW="250px">
              <SidebarContent />
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        <SidebarContent />
      )}
    </>
  );
};

export default AssociadoSidebar;